import { Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { IconDownload, IconMail } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';

import 'dayjs/locale/fr';

import { notifications } from '@mantine/notifications';

import { trpc } from '../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../services/upload';

dayjs.locale('fr');

export const Route = createFileRoute('/app/projets/$slug/lettres/')({
  component: RouteComponent,
});

function formatLetterType(type: string): string {
  return type
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function RouteComponent() {
  const params = Route.useParams();
  const { data: project } = trpc.projects.get.useQuery({ slug: params.slug });
  const { data: letters, isLoading } = trpc.letters.list.useQuery(
    { projectId: project?.id ?? '' },
    { enabled: !!project?.id },
  );
  const generateLetter = trpc.letters.generate.useMutation({
    onError: error => {
      notifications.show({
        title: 'Erreur',
        message: error.message,
        color: 'red',
      });
    },
  });

  if (!project) {
    return null;
  }

  if (isLoading) {
    return (
      <Stack>
        <Title order={3}>Lettres</Title>
        <Card>
          <Text c="dimmed">Chargement...</Text>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack className="max-w-xl">
      <Group justify="space-between" align="center">
        <Title order={3}>Historique des lettres</Title>
        {letters && letters.length > 0 && (
          <Badge size="lg" variant="outline">
            {letters.length}
          </Badge>
        )}
      </Group>
      <Group>
        <Button
          variant="outline"
          leftSection={<IconDownload size={16} />}
          loading={
            generateLetter.isPending &&
            generateLetter.variables?.letterType === 'template'
          }
          onClick={async () => {
            const letter = await generateLetter.mutateAsync({
              projectId: project.id,
              letterType: 'template',
            });
            if (letter) {
              const blob = new Blob([new Uint8Array(letter)], {
                type: 'application/pdf',
              });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }
          }}
        >
          Générer lettre vide
        </Button>

        <Button
          variant="outline"
          leftSection={<IconDownload size={16} />}
          loading={
            generateLetter.isPending &&
            generateLetter.variables?.letterType ===
              '8-demande-dacompte-35-avec-lettre-rib'
          }
          onClick={async () => {
            const letter = await generateLetter.mutateAsync({
              projectId: project.id,
              letterType: '8-demande-dacompte-35-avec-lettre-rib',
            });
            if (letter) {
              const blob = new Blob([new Uint8Array(letter)], {
                type: 'application/pdf',
              });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }
          }}
        >
          Générer demande d'acompte{' '}
          {project.paymentPercentRequiredOnSignature?.toNumber() ?? 35}%
        </Button>

        <Button
          variant="outline"
          leftSection={<IconDownload size={16} />}
          loading={
            generateLetter.isPending &&
            generateLetter.variables?.letterType ===
              '28-demande-de-reglement-de-la-mise-a-disposition-de-la-marchandise'
          }
          onClick={async () => {
            const letter = await generateLetter.mutateAsync({
              projectId: project.id,
              letterType:
                '28-demande-de-reglement-de-la-mise-a-disposition-de-la-marchandise',
            });
            if (letter) {
              const blob = new Blob([new Uint8Array(letter)], {
                type: 'application/pdf',
              });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }
          }}
        >
          Générer demande de règlement de la mise à disposition de la
          marchandise
        </Button>
        <Button
          variant="outline"
          leftSection={<IconDownload size={16} />}
          loading={
            generateLetter.isPending &&
            generateLetter.variables?.letterType ===
              '36-demande-reglement-solde'
          }
          onClick={async () => {
            const letter = await generateLetter.mutateAsync({
              projectId: project.id,
              letterType: '36-demande-reglement-solde',
            });
            if (letter) {
              const blob = new Blob([new Uint8Array(letter)], {
                type: 'application/pdf',
              });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }
          }}
        >
          Générer demande de règlement du solde
        </Button>
        <Button
          variant="outline"
          leftSection={<IconDownload size={16} />}
          loading={
            generateLetter.isPending &&
            generateLetter.variables?.letterType ===
              '37-relance-reglement-solde'
          }
          onClick={async () => {
            const letter = await generateLetter.mutateAsync({
              projectId: project.id,
              letterType: '37-relance-reglement-solde',
            });
            if (letter) {
              const blob = new Blob([new Uint8Array(letter)], {
                type: 'application/pdf',
              });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }
          }}
        >
          Générer relance de règlement du solde
        </Button>
      </Group>
      {!letters || letters.length === 0 ? (
        <Card withBorder>
          <Text c="dimmed" ta="center" py="xl">
            Aucune lettre envoyée pour ce projet
          </Text>
        </Card>
      ) : (
        <Stack gap="xs">
          {letters.map(letter => (
            <Card key={letter.id} withBorder padding="sm">
              <Group justify="space-between" align="center">
                <Group gap="xs" style={{ flex: 1 }}>
                  <IconMail size={16} />
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>
                        {formatLetterType(letter.type)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(letter.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </Group>
                    <Stack gap={2}>
                      {letter.sentTo.map(recipient => (
                        <Text key={recipient.email} size="xs" c="dimmed">
                          {recipient.name} - {recipient.email}
                        </Text>
                      ))}
                    </Stack>
                  </Stack>
                </Group>
              </Group>
              <Group justify="end">
                {letter.attachmentUtFileKey && (
                  <Button
                    leftSection={<IconDownload size={14} />}
                    variant="light"
                    size="xs"
                    onClick={() => {
                      window.open(
                        getUTDocumentUrl(letter.attachmentUtFileKey!),
                        '_blank',
                      );
                    }}
                  >
                    Lettre
                  </Button>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
